/**
 * PRIMITIVES - references to the NBX primitive tokens.
 *
 * The values live in libs/styles/src/tokens/nbx.tokens.json and reach the page as `--nbx-*`
 * custom properties (generated/nbx-tokens.css). Every export here is a `var(--nbx-…)`
 * reference with the same shape the preset factories always consumed, so the PrimeNG preset
 * never carries a literal value and follows the active NBX mode at runtime.
 *
 * Token Hierarchy:
 * nbx.tokens.json → --nbx-* CSS → Primitives (this file) → Aliases → Semantic → PrimeNG preset
 */
import { type NbxTokenId, nbxVar } from '../../tokens/generated/nbx-tokens.js';

const SHADES = [
  '50',
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '950',
] as const;
type Shade = (typeof SHADES)[number];
type Scale = Record<Shade, string>;

/** A full 50–950 palette of references to one NBX colour family. */
function scale(family: string): Scale {
  return Object.fromEntries(
    SHADES.map(shade => [shade, nbxVar(`color.${family}.${shade}` as NbxTokenId)])
  ) as Scale;
}

// ============================================
// COLOR PALETTES
// ============================================

/** Rentsody Blue - Secondary/Neutral - Global core brand */
export const rentsodyBlue = scale('rentsody');

/** Rentals Magenta - Primary - Brand hero color for Rentals */
export const rentalsMagenta = scale('rentals');

/** Green - Success states */
export const green = scale('success');

/** Red - Danger/Error states */
export const red = scale('danger');

/** Yellow - Warning states */
export const yellow = scale('warning');

/** Home Aquamarine - Sub-brand: Home product */
export const homeAquamarine = scale('home');

/** Lock Orange - Sub-brand: Lock product */
export const lockOrange = scale('lock');

/** SSR Blue - Primary color for the SSR Guest Portal */
export const ssrBlue = scale('ssr');

/** Guest Purple - Sub-brand: Guest product */
export const guestPurple = scale('guest');

/** Neutral palette - Based on Rentsody Blue values */
export const neutral = { white: nbxVar('color.white'), ...scale('neutral') };

// ============================================
// SPACING (4px grid system)
// ============================================

export const spacing = {
  4: nbxVar('spacing.4'),
  8: nbxVar('spacing.8'),
  16: nbxVar('spacing.16'),
  24: nbxVar('spacing.24'),
  32: nbxVar('spacing.32'),
  40: nbxVar('spacing.40'),
  48: nbxVar('spacing.48'),
  56: nbxVar('spacing.56'),
  64: nbxVar('spacing.64'),
} as const;

// ============================================
// BORDER RADIUS
// ============================================

export const borderRadius = {
  4: nbxVar('radius.4'),
  8: nbxVar('radius.8'),
  16: nbxVar('radius.16'),
  24: nbxVar('radius.24'),
} as const;

// ============================================
// SHADOWS
// ============================================

export const shadows = {
  sm: nbxVar('shadow.sm'),
  md: nbxVar('shadow.md'),
  lg: nbxVar('shadow.lg'),
  card: nbxVar('shadow.card'),
} as const;

// ============================================
// TYPOGRAPHY
// ============================================

export const fontFamily = {
  base: nbxVar('font.family.base'),
} as const;

export const fontSize = {
  xs: nbxVar('text.size.xs'),
  sm: nbxVar('text.size.sm'),
  base: nbxVar('text.size.base'),
  lg: nbxVar('text.size.lg'),
  xl: nbxVar('font-size.xl'),
  '2xl': nbxVar('font-size.2xl'),
  '3xl': nbxVar('font-size.3xl'),
  '4xl': nbxVar('font-size.4xl'),
  '5xl': nbxVar('font-size.5xl'),
} as const;

export const fontWeight = {
  normal: nbxVar('font.weight.normal'),
  semibold: nbxVar('font.weight.semibold'),
  bold: nbxVar('font.weight.bold'),
} as const;

export const lineHeight = {
  base: nbxVar('text.line-height.xs'),
  lg: nbxVar('text.line-height.sm'),
  xl: nbxVar('text.line-height.base'),
  '2xl': nbxVar('text.line-height.2xl'),
  '3xl': nbxVar('line-height.3xl'),
  '4xl': nbxVar('line-height.4xl'),
  '5xl': nbxVar('line-height.5xl'),
} as const;
